// /connect#lat_cx_…: the single-use link an agent sends the operator to
// sign in to a product for it. The ticket is the proof: no Lattice session
// is needed (rendered before the auth gate, like /set-password), and the
// token is in the fragment, which a browser never sends: it is read once,
// kept for this tab only (sessionStorage, so a reload still finds it) and
// stripped from the address bar, and the page holds app updates while the
// link is live. Peek never spends it; one press of the button does, and the
// vendor's consent (an https address only, signIn.ts) opens in this tab.
// Always Safari on the phone (companion/nativePaths.ts): inside the app's
// web view the page offers "Open in Safari" (a new window, which the app
// hands to Safari) instead of opening a consent there.

import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";

import { peekTicket, startTicket, type TicketInfo } from "@/api/connections";
import { useUpdateHold } from "@/components/pwa/updateHold";
import { Button, buttonClasses } from "@/components/ui/Button";
import { SIGN_IN_REFUSED_LINE, ticketDeadLine, ticketFrom } from "@/features/settings/connections/connectionsModel";
import { isHttpsUrl } from "@/features/settings/connections/signIn";
import { ApiError, errorCode, errorText } from "@/lib/api";
import { isEmbedded } from "@/native/embedded";
import { ambientLight } from "@/theme";

type State = { kind: "checking" } | { kind: "ready"; ticket: TicketInfo } | { kind: "starting"; ticket: TicketInfo } | { kind: "dead"; message: string };

/** This tab's copy of the ticket (gone with the tab, or once the ticket is spent or dead). */
const KEPT = "lattice.connect.ticket";

function readTicket(): string | null {
  let kept: string | null = null;
  try {
    kept = window.sessionStorage.getItem(KEPT);
  } catch {
    // no storage: the fragment alone
  }
  const token = ticketFrom(window.location.hash, kept);
  try {
    if (token) window.sessionStorage.setItem(KEPT, token);
  } catch {
    // as above
  }
  return token;
}

function forgetTicket(): void {
  try {
    window.sessionStorage.removeItem(KEPT);
  } catch {
    // nothing kept
  }
}

function deadLine(e: unknown): string | null {
  if (e instanceof ApiError && (e.status === 404 || e.status === 410)) return ticketDeadLine(errorCode(e));
  return null;
}

export function ConnectTicket() {
  const [token] = useState(readTicket);
  const [state, setState] = useState<State>(token ? { kind: "checking" } : { kind: "dead", message: ticketDeadLine(null) });
  const [error, setError] = useState("");
  const embedded = isEmbedded();
  // A background update would reload this page while the link is still to be used.
  useUpdateHold(state.kind !== "dead");

  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
  }, []);

  useEffect(() => {
    if (!token) return;
    let live = true;
    peekTicket(token).then(
      (ticket) => live && setState({ kind: "ready", ticket }),
      (e: unknown) => {
        if (!live) return;
        const dead = deadLine(e);
        if (dead) forgetTicket();
        setState({ kind: "dead", message: dead ?? errorText(e) });
      },
    );
    return () => {
      live = false;
    };
  }, [token]);

  async function start() {
    if (state.kind !== "ready" || !token) return;
    setError("");
    setState({ kind: "starting", ticket: state.ticket });
    try {
      const { authorize_url } = await startTicket(token);
      forgetTicket();
      if (!isHttpsUrl(authorize_url)) throw new Error(SIGN_IN_REFUSED_LINE);
      window.location.assign(authorize_url);
    } catch (e) {
      const dead = deadLine(e);
      if (dead) {
        forgetTicket();
        setState({ kind: "dead", message: dead });
      } else {
        setState({ kind: "ready", ticket: state.ticket });
        setError(errorText(e));
      }
    }
  }

  const ticket = state.kind === "ready" || state.kind === "starting" ? state.ticket : null;
  const product = ticket?.connector_name || ticket?.connector || "";
  const agent = ticket?.agent_name || ticket?.agent || "";

  return (
    <div className="flex app-vh w-full flex-col items-center overflow-y-auto pt-safe pb-safe" style={{ background: ambientLight }}>
      <main className="w-full max-w-[432px] px-4 py-12 sm:my-auto sm:py-10">
        <div className="sm:rounded-card sm:border sm:border-line sm:bg-panel sm:p-8 sm:shadow-[var(--shadow-panel)]">
          <img src="/corvus-logo.svg" alt="Corvus Research" className="h-10 w-auto" />
          <h1 className="mt-8 font-display text-2xl tracking-[var(--font-display-tracking)] text-fg">
            {ticket ? `Connect ${product} for ${agent}` : "Connect"}
          </h1>

          {state.kind === "checking" && (
            <p role="status" className="mt-2 text-sm text-fg-muted">
              Checking your link…
            </p>
          )}

          {state.kind === "dead" && (
            <p role="alert" className="mt-3 flex items-start gap-2 text-base text-fg">
              <AlertCircle aria-hidden className="mt-1 h-4 w-4 shrink-0 text-danger" />
              {state.message}
            </p>
          )}

          {ticket && (
            <div className="mt-6 flex flex-col gap-4">
              {ticket.instance_label && <p className="text-sm text-fg-muted">{ticket.instance_label}</p>}
              {embedded && token ? (
                <a
                  className={buttonClasses({ size: "lg", className: "w-full" })}
                  href={`${window.location.origin}/connect#${token}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in Safari
                </a>
              ) : (
                <Button size="lg" className="w-full" loading={state.kind === "starting"} onClick={() => void start()}>
                  Sign in to {product}
                </Button>
              )}
              {error && (
                <p role="alert" className="flex items-start gap-2 text-sm text-danger">
                  <AlertCircle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
                  {error}
                </p>
              )}
            </div>
          )}
        </div>
        <p className="mt-8 text-center text-xs text-fg-subtle">Corvus Agent Platform</p>
      </main>
    </div>
  );
}
